import { getProjectAmounts } from '@bulthaup/api/entities/budget';
import { Group, Stack, Text, Title } from '@mantine/core';
import { DateInput } from '@mantine/dates';
import dayjs from 'dayjs';
import Decimal from 'decimal.js';

import { trpc } from '../../../../../../services/trpc';
import { formatAmount } from '../../../../../../utils/format';

export function FinalBalance({ customerId }: { customerId: string }) {
  const customer = trpc.projects.getCustomer.useQuery({ id: customerId });
  const updateCustomerInvoicedAtMutation =
    trpc.projects.updateCustomerInvoicedAt.useMutation();

  const { data: payments = [] } = trpc.projects.listPayments.useQuery({
    customerId,
  });

  const { data: credits = [] } = trpc.projects.listCredits.useQuery({
    customerId,
  });

  const { data: projects = [] } = trpc.projects.listCustomerProjects.useQuery({
    customerId,
  });

  const grossTotalAmount = projects.reduce((acc, project) => {
    const gross = Object.values(getProjectAmounts(project).gross);
    const projectTotal = gross.reduce(
      (sum, val) => sum.add(val),
      new Decimal(0),
    );
    return acc.add(projectTotal);
  }, new Decimal(0));

  const totalPayments = payments.reduce(
    (acc, payment) => acc.add(payment.amount ?? 0),
    new Decimal(0),
  );

  const creditsTotal = credits.reduce(
    (acc, credit) => acc.add(credit.amount ?? 0),
    new Decimal(0),
  );

  const finalBalance = grossTotalAmount
    .sub(totalPayments)
    .sub(creditsTotal)
    .toDecimalPlaces(2);

  const paidPercent = grossTotalAmount.greaterThan(0)
    ? totalPayments.add(creditsTotal).div(grossTotalAmount).mul(100)
    : new Decimal(0);

  const isPaid = paidPercent.gte(99.9);

  return (
    <Stack>
      <Group justify="space-between">
        <Text size="sm" fw={700} c="dimmed">
          Facture envoyée le :
        </Text>
        <DateInput
          valueFormat="DD/MM/YYYY"
          clearable
          value={customer.data?.invoicedAt}
          onChange={async date => {
            await updateCustomerInvoicedAtMutation.mutateAsync({
              id: customerId,
              invoicedAt: date ? dayjs(date).toDate() : null,
            });
          }}
          placeholder="Sélectionner une date"
          style={{ flex: 1, maxWidth: 200 }}
        />
      </Group>
      <Group justify="space-between">
        <Text size="sm" fw={700} c="dimmed">
          Total des avoirs:
        </Text>
        <Text size="sm" fw={700} c="dimmed">
          {formatAmount(creditsTotal)}
        </Text>
      </Group>
      <Group justify="space-between">
        <Text size="sm" fw={700}>
          Solde final:
        </Text>
        <Title
          order={2}
          size="h2"
          style={{ textAlign: 'center', width: '100%' }}
        >
          {formatAmount(finalBalance)}
        </Title>
        <Title
          order={5}
          size="sm"
          c={isPaid ? 'green' : 'red'}
          style={{ textAlign: 'center', width: '100%' }}
          tt="uppercase"
          fw={700}
        >
          {isPaid ? 'Soldé' : 'Non soldé'}
        </Title>
      </Group>
    </Stack>
  );
}
